// File: src/components/notifications/NotificationsModal.js

import React, { useState, useEffect, useCallback, useMemo } from 'react'; 
import { useNavigate } from 'react-router-dom'; // 🌟 1. Import useNavigate for internal links
import { useProfile } from '../../context/ProfileContext';
import { 
  getFirestore, 
  doc, 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs, 
  onSnapshot, 
  setDoc, 
  serverTimestamp 
} from 'firebase/firestore';

const PAGE_SIZE = 15;

const NotificationItem = React.memo(({ notification, isDarkMode, onCloseModal }) => {
  const navigate = useNavigate();

  // 🌟 2. Handle embedded <a> tag clicks cleanly
  const handleContentClick = (e) => {
    const targetLink = e.target.closest('a');
    if (!targetLink) return;

    const href = targetLink.getAttribute('href');
    if (!href) return;

    // External link: Let default browser navigation open in a new tab
    if (href.startsWith('http://') || href.startsWith('https://')) {
      // Ensure target="_blank" and rel="noopener noreferrer" are set
      targetLink.setAttribute('target', '_blank');
      targetLink.setAttribute('rel', 'noopener noreferrer');
      return;
    }

    // Internal SPA route: Prevent full reload, close modal, and navigate
    e.preventDefault();
    if (onCloseModal) onCloseModal();
    navigate(href);
  };

  return (
    <div 
      className={`flex items-center p-4 border-b transition-all duration-200 ${
        isDarkMode 
          ? 'border-zinc-800/60 hover:bg-zinc-950/20' 
          : 'border-slate-100 hover:bg-slate-50/40'
      }`}
      style={{ opacity: notification.isRead ? 0.5 : 1 }}
    >
      <div className={`w-2 h-2 rounded-full mr-3.5 flex-shrink-0 ${
        notification.isRead 
          ? isDarkMode ? 'bg-zinc-700' : 'bg-emerald-400' 
          : 'bg-brand-pink animate-pulse'
      }`} />
      
      {/* 🌟 3. Attach click delegation handler & styling for interactive links */}
      <span 
        className={`text-xs font-semibold leading-relaxed tracking-wide [&_a]:underline [&_a]:text-brand-pink [&_a]:font-bold [&_a]:hover:opacity-80 ${
          isDarkMode ? 'text-zinc-300' : 'text-slate-700'
        }`}
        onClick={handleContentClick}
        dangerouslySetInnerHTML={{ __html: notification.message }} 
      />
    </div>
  );
});

const NotificationsModal = ({ isOpen, onClose }) => {
  const { activeProfile, isDarkMode } = useProfile(); 
  const artistId = activeProfile?.type === 'artist' ? activeProfile.id : null;

  const [notifications, setNotifications] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreArchive, setHasMoreArchive] = useState(true);
  const db = getFirestore();

  // 1. LISTEN TO SUMMARY DOCUMENT
  useEffect(() => {
    if (!isOpen || activeProfile?.type !== 'artist' || !artistId) {
      setNotifications([]);
      setVisibleCount(PAGE_SIZE);
      return;
    }

    setLoading(true);
    const summaryDocRef = doc(db, 'artist_accounts', artistId, 'artist_notifications', 'summary');

    const unsubscribe = onSnapshot(summaryDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const items = data.items || [];
        setNotifications(items);
        
        if (items.length < 100) {
          setHasMoreArchive(false);
        }
      } else {
        setNotifications([]);
        setHasMoreArchive(false);
      }
      setLoading(false);
    }, (err) => {
      console.error("Error listening to notification summary:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isOpen, activeProfile, artistId, db]);

  // 2. TWO-TIER PAGINATION HANDLER
  const handleLoadMore = async () => {
    if (isLoadingMore) return;

    if (visibleCount < notifications.length) {
      setVisibleCount(prev => Math.min(prev + PAGE_SIZE, notifications.length));
      return;
    }

    if (!hasMoreArchive || notifications.length === 0 || !artistId) return;

    setIsLoadingMore(true);
    try {
      const oldestNotif = notifications[notifications.length - 1];
      const archiveRef = collection(db, 'artist_accounts', artistId, 'artist_notifications');

      const cursorValue = oldestNotif.createdAt 
        ? new Date(oldestNotif.createdAt) 
        : new Date();

      const q = query(
        archiveRef,
        orderBy('createdAt', 'desc'),
        startAfter(cursorValue),
        limit(PAGE_SIZE)
      );

      const querySnap = await getDocs(q);

      if (querySnap.empty) {
        setHasMoreArchive(false);
      } else {
        const newArchivedItems = [];
        querySnap.forEach((docSnap) => {
          if (docSnap.id === 'summary') return;
          const data = docSnap.data();
          newArchivedItems.push({
            id: docSnap.id,
            category: data.category || 'fans',
            message: data.message || '',
            isRead: Boolean(data.isRead),
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt
          });
        });

        const existingIds = new Set(notifications.map(n => n.id));
        const filteredNewItems = newArchivedItems.filter(item => !existingIds.has(item.id));

        if (filteredNewItems.length < PAGE_SIZE) {
          setHasMoreArchive(false);
        }

        setNotifications(prev => [...prev, ...filteredNewItems]);
        setVisibleCount(prev => prev + filteredNewItems.length);
      }
    } catch (error) {
      console.error("Error loading archived notifications:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderedNotifications = useMemo(() => {
    return notifications.slice(0, visibleCount);
  }, [notifications, visibleCount]);

  const canLoadMore = visibleCount < notifications.length || hasMoreArchive;

  const markAllAsRead = useCallback(async () => {
    if (!artistId || notifications.length === 0) return;

    let hasChanges = false;
    const updatedNotifications = notifications.map(n => {
      if (!n.isRead) {
        hasChanges = true;
        return { ...n, isRead: true };
      }
      return n;
    });

    if (hasChanges) {
      const summaryDocRef = doc(db, 'artist_accounts', artistId, 'artist_notifications', 'summary');

      try {
        await setDoc(summaryDocRef, {
          items: updatedNotifications.slice(0, 100),
          unreadCount: 0,
          lastReadAt: serverTimestamp()
        }, { merge: true });
      } catch (err) {
        console.error("Failed to update notification read states:", err);
      }
    }
  }, [notifications, artistId, db]);

  const handleClose = async () => {
    await markAllAsRead();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div 
        className={`rounded-2xl shadow-2xl w-full max-w-lg flex flex-col h-[520px] max-h-[80vh] overflow-hidden border transition-all duration-300 relative ${
          isDarkMode 
            ? "bg-zinc-900 border-zinc-800 shadow-black/60 text-white" 
            : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-4 border-b flex justify-between items-center ${
          isDarkMode ? 'border-zinc-800 bg-zinc-950/40' : 'border-slate-100 bg-slate-50'
        }`}>
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider italic">Notifications</h3>
            <p className="text-[10px] text-gray-400 font-medium">Activity & Updates</p>
          </div>
          <button 
            className={`text-xl font-bold transition-colors focus:outline-none ${
              isDarkMode ? 'text-zinc-500 hover:text-white' : 'text-slate-400 hover:text-slate-800'
            }`}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
          {loading ? (
            <p className={`text-center text-[10px] font-black uppercase tracking-widest my-12 animate-pulse ${
              isDarkMode ? 'text-zinc-600' : 'text-gray-400'
            }`}>
              Syncing System Ledgers...
            </p>
          ) : renderedNotifications.length > 0 ? (
            <>
              {renderedNotifications.map(n => (
                <NotificationItem 
                  key={n.id} 
                  notification={n} 
                  isDarkMode={isDarkMode} 
                  onCloseModal={handleClose}
                />
              ))}

              {/* Load More Button */}
              {canLoadMore && (
                <div className="p-4 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all active:scale-95 ${
                      isDarkMode
                        ? "bg-zinc-800 border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-700"
                        : "bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {isLoadingMore ? "Loading History..." : "Load 15 More"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className={`text-center text-xs font-bold uppercase tracking-widest my-16 italic ${
              isDarkMode ? 'text-zinc-600' : 'text-gray-400'
            }`}>
              No notifications yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsModal;